import { useMoviesStore } from "@multicines/stores";
import { CONSTANTS, getCalendarDays } from "@multicines/utils";
import dayjs, { Dayjs } from "dayjs";
import ArrowLeftSVG from "images/arrow-left.svg";
import ArrowRightSVG from "images/arrow-right.svg";
import React, { useEffect } from "react";
import { useMemo, useRef } from "react";

import Styles from "./Days.styles";
import { DaysProps as Props } from "./Days.types";
import Day from "../Day/Day";
import Slider from "../Slider/Slider";

const { DAYS } = CONSTANTS.DATE;

const Days: React.FC<Props> = props => {
  const { className, preReleaseDate, isReady, disabled } = props;
  const globalDate = useMoviesStore(store => store.globalDate);
  const selectedDateStore = useMoviesStore(store => store.selectedDate);
  const setSelectedDateStore = useMoviesStore(store => store.setSelectedDate);
  const parentRef = useRef<any>(null);
  const ref = useRef(false);

  const days = useMemo(() => getCalendarDays(DAYS), []);

  const setSelectedDayHandler = (date: Dayjs) => {
    setSelectedDateStore(date);
  };

  const prevArrow = (
    <div>
      <div className="Days__left-arrow">
        <ArrowLeftSVG />
      </div>
    </div>
  );

  const nextArrow = (
    <div className={className}>
      <div className="Days__right-arrow">
        <ArrowRightSVG />
      </div>
    </div>
  );

  const scrollToSelectedDay = (date: Dayjs) => {
    const index = days.findIndex(day => {
      const sameMonth = day.format("M") === date.format("M");
      const sameDay = day.format("D") === date.format("D");
      return sameMonth && sameDay;
    });
    if (index < 0) return;

    parentRef?.current?.slickGoTo(index, true);
  };

  //INFO: Execute only first render when the movie is ready
  useEffect(() => {
    if (ref.current) return;
    if (!isReady || !globalDate) return;

    if (!preReleaseDate) {
      setSelectedDayHandler(globalDate);
      scrollToSelectedDay(globalDate);
      ref.current = true;
      return;
    }

    const preReleaseDateIsBefore = dayjs(preReleaseDate).isBefore(globalDate);
    if (preReleaseDateIsBefore) {
      setSelectedDayHandler(globalDate);
      scrollToSelectedDay(globalDate);
      ref.current = true;
      return;
    }

    const newPreReleaseDate = preReleaseDate?.slice(0, 10);
    const formatPreReleaseDate = dayjs(newPreReleaseDate);
    setSelectedDayHandler(formatPreReleaseDate);
    scrollToSelectedDay(formatPreReleaseDate);
    ref.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDate, isReady]);

  return (
    <Styles className={`Days ${className}`}>
      <Slider
        className="Days__slider"
        slideWidth={64}
        height={88}
        gap={32}
        prevArrow={prevArrow}
        nextArrow={nextArrow}
        arrows
        parentRef={parentRef}
      >
        {days.map((day, index) => {
          return (
            <Day
              key={index}
              date={day}
              disabled={disabled}
              selectedDate={selectedDateStore}
              onClick={setSelectedDayHandler}
            />
          );
        })}
      </Slider>
    </Styles>
  );
};

export default Days;
